import { IResourceListProps } from "../../common/ra-types";
import {
  Datagrid,
  Filter,
  ListContext,
  NumberField,
  Pagination,
  ResourceContextProvider,
  TextField,
} from "react-admin";
import { addDays, endOfQuarter, startOfQuarter } from "date-fns";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";
import classes from "./StateTripsList.module.css";
import resourceNames from "../../../common/data/resourceNames";
import { CompactListBase } from "../../common/form/CompactListBase";
import { useState } from "react";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import permissionNames from "../../../common/auth/access-control/permissionNames";

interface IListFilterProps {
  onDateFromChange: (value: any) => void;
  onDateToChange: (value: any) => void;
  initialValues: { dateFrom: Date; dateTo: Date };
  resource: string;
  basePath: string;
}

const getDefaultDateFrom = () => {
  const now = new Date();
  const date = startOfQuarter(addDays(startOfQuarter(now), -1));
  return date > now ? now : date;
};

const getDefaultDateTo = () => {
  const now = new Date();
  const date = endOfQuarter(addDays(startOfQuarter(now), -1));
  return date > now ? now : date;
};

const ListFilter = ({
  onDateFromChange,
  onDateToChange,
  initialValues,
  ...rest
}: IListFilterProps) => {
  return (
    <ListContext.Provider
      value={{
        ...rest,
        currentSort: { field: "", order: "" },
        ids: [],
        data: {},
        loading: false,
        loaded: true,
        onSelect: () => {},
        onToggleItem: () => {},
        onUnselectItems: () => {},
        setSort: () => {},
        total: 0,
        page: 0,
        perPage: 0,
        refetch: () => {},
        selectedIds: [],
        setPage: () => {},
        setPerPage: () => {},
        filterValues: initialValues,
        displayedFilters: null,
        hideFilter: () => {},
        setFilters: (value: any) => {
          console.log("setFilters", value);
          if (value?.dateFrom) {
            onDateFromChange(value.dateFrom);
          }

          if (value?.dateTo) {
            onDateToChange(value.dateTo);
          }
        },
        showFilter: () => {},
      }}
    >
      <Filter>
        <GenericDateOrTimePicker
          PickerComponent={DatePicker}
          className="list-view-filter"
          label="Date from"
          source="dateFrom"
          alwaysOn
          options={{
            minDate: new Date("2024-01-01T06:00:00Z"),
            maxDate: new Date(),
          }}
        />
        <GenericDateOrTimePicker
          PickerComponent={DatePicker}
          className="list-view-filter"
          label="Date to"
          source="dateTo"
          alwaysOn
          options={{
            minDate: new Date("2024-01-01T06:00:00Z"),
            maxDate: new Date(),
          }}
        />
      </Filter>
    </ListContext.Provider>
  );
};

export const StateTripsList = (props: IResourceListProps) => {
  const [dateFrom, setDateFrom] = useState<Date>(getDefaultDateFrom());
  const [dateTo, setDateTo] = useState<Date>(getDefaultDateTo());

  const permissions = useActionPermissions();

  return (
    <div className={classes.page}>
      <div className={classes.listFilter}>
        <ListFilter
          basePath={props.basePath!}
          resource={props.resource!}
          initialValues={{ dateFrom, dateTo }}
          onDateFromChange={(value: Date) => setDateFrom(value)}
          onDateToChange={(value: Date) => setDateTo(value)}
        />
      </div>
      <div className={classes.wrapper}>
        <div className={classes.list}>
          <ResourceContextProvider
            value={resourceNames.LOCATIONS_STATE_TRIPS_BY_STATE}
          >
            <CompactListBase
              {...props}
              perPage={25}
              sort={{ field: "stateCode", order: "ASC" }}
              filter={{
                dateFrom,
                dateTo,
              }}
              resource={resourceNames.LOCATIONS_STATE_TRIPS_BY_STATE}
            >
              <div className={classes.listWrapper}>
                <Datagrid
                  rowClick={
                    permissions[permissionNames.locations.history.main]
                      ? (id, _basePath, _record) => {
                          return `/${
                            resourceNames.LOCATIONS_STATE_TRIPS
                          }/state/${id}?dateFrom=${dateFrom.toISOString()}&dateTo=${dateTo.toISOString()}`;
                        }
                      : undefined
                  }
                >
                  <TextField source="stateCode" label="State" />
                  <NumberField source="distance" label="Distance" />
                </Datagrid>
              </div>
              <Pagination rowsPerPageOptions={[25, 50, 100]} />
            </CompactListBase>
          </ResourceContextProvider>
        </div>
        <div className={classes.list}>
          <ResourceContextProvider
            value={resourceNames.LOCATIONS_STATE_TRIPS_BY_VEHICLE}
          >
            <CompactListBase
              {...props}
              perPage={25}
              sort={{ field: "name", order: "ASC" }}
              resource={resourceNames.LOCATIONS_STATE_TRIPS_BY_VEHICLE}
              filter={{
                dateFrom: dateFrom,
                dateTo: dateTo,
              }}
            >
              <div className={classes.listWrapper}>
                <Datagrid
                  rowClick={
                    permissions[permissionNames.locations.history.main]
                      ? (id, _basePath, _record) => {
                          return `/${
                            resourceNames.LOCATIONS_STATE_TRIPS
                          }/vehicle/${id}?dateFrom=${dateFrom.toISOString()}&dateTo=${dateTo.toISOString()}`;
                        }
                      : undefined
                  }
                >
                  <TextField source="name" label="Vehicle" />
                  <NumberField source="distance" label="Distance" />
                </Datagrid>
              </div>
              <Pagination rowsPerPageOptions={[25, 50, 100]} />
            </CompactListBase>
          </ResourceContextProvider>
        </div>
      </div>
    </div>
  );
};
